import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { resetPassword } from "services";
import { clearGuestUser } from "store";
import {
  BetaForm as useBetaForm,
  PasswordTextInputBase,
  ButtonBase,
} from "@reusejs/react";
import { useRouter } from "next/router";
import { CheckCircleIcon } from "@heroicons/react/outline";
import Link from "next/link";
import {
  getUser,
  getSubscriptionByUserUuid,
  checkAndUpdateBidder,
} from "services";
import { webStore, setTokens, setUser, setSubscription } from "store";

export default function ResetPasswordForm() {
  const dispatch = useDispatch();
  const router: any = useRouter();
  const [success, setSuccess] = useState(false);

  const form = useBetaForm({
    user_uuid: router.query.user_uuid,
    token: router.query.token,
    password: "",
    confirm_password: "",
  });

  const form_constraints = {
    user_uuid: {
      presence: {
        allowEmpty: false,
      },
    },
    token: {
      presence: {
        allowEmpty: false,
      },
    },
    password: {
      presence: {
        allowEmpty: false,
      },
      length: {
        minimum: 8,
      },
    },
    confirm_password: {
      presence: {
        allowEmpty: false,
      },
      equality: "password",
    },
  };

  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, []);

  const handleResetPassword = async () => {
    try {
      form.value.user_uuid = router.query.user_uuid;
      form.value.token = router.query.token;

      if (form.validate()) {
        await resetPassword(form).then(async (res: any) => {
          dispatch(clearGuestUser());
          setSuccess(true);
          webStore.dispatch(setTokens({ access_token: res.access_token }));
          const user: any = await getUser();
          webStore.dispatch(setUser(user));

          const subscription = await getSubscriptionByUserUuid(user.uuid);
          const validateBidderEmail = await checkAndUpdateBidder(
            user?.attributes[0]?.value,
            user.uuid
          );

          if (subscription) {
            webStore.dispatch(setSubscription(subscription));
          }
          router.push("/");
        });
      } else {
        console.log("check err:", form.value);
      }
    } catch (error) {
      console.log("check here forgot error", error);
    }
  };

  const handleContinue = () => {
    router.push("/");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-20 xl:px-24 flex">
      <div className="mx-auto w-full max-w-md lg:w-96 ">
        <div>
          {!success ? (
            <>
              <h2 className="mt-6 text-3xl font-semibold text-gray-900 tracking-tight">
                Set a new password
              </h2>
              <p className="mt-3 text-base text-gray-600">
                Your new password must be different to previously used
                passwords.
              </p>

              <div className="mt-4">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <div className="mt-1">
                  <PasswordTextInputBase
                    htmlFor={"password"}
                    name="password"
                    onChange={(val) => form.setField("password", val)}
                    type="password"
                    value={form.getField("password")}
                    placeholder="Password"
                    error={
                      form.errors.get("password") ? (
                        <p className="mt-2 text-sm text-red-500">
                          {form.errors.get("password")[0]}
                        </p>
                      ) : null
                    }
                  />
                </div>
              </div>
              <div className="mt-4">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm password
                </label>
                <div className="mt-1">
                  <PasswordTextInputBase
                    htmlFor={"confirm_password"}
                    name="confirm_password"
                    onChange={(val) => form.setField("confirm_password", val)}
                    type="password"
                    value={form.getField("confirm_password")}
                    placeholder="confirm password"
                    error={
                      form.errors.get("confirm_password") ? (
                        <p className="mt-2 text-sm text-red-500">
                          {form.errors.get("confirm_password")[0]}
                        </p>
                      ) : null
                    }
                  />
                </div>
              </div>

              <div className="mt-6">
                <ButtonBase
                  variant="primary"
                  type="submit"
                  label="Reset password"
                  onClick={() => {
                    handleResetPassword();
                  }}
                  buttonBaseClasses={{
                    font: "text-sm font-medium",
                    backgroundColor: "bg-blue-600 hover:bg-blue-700",
                    textColor: "text-white",
                    border: "any",
                    width: "w-full",
                    padding: "px-4 py-3",
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <CheckCircleIcon
                height={30}
                width={30}
                className="text-blue-700"
              />
              <h2 className="mt-6 text-3xl font-semibold text-gray-900 tracking-tight">
                Password reset
              </h2>
              <p className="mt-3 text-base text-gray-600">
                Your password has been successfully reset. Click below to log in
                magically.
              </p>

              <div className="mt-4">
                <ButtonBase
                  variant="primary"
                  type="submit"
                  label="Continue"
                  onClick={() => {
                    handleContinue();
                  }}
                  buttonBaseClasses={{
                    font: "text-sm font-medium",
                    backgroundColor: "bg-blue-600 hover:bg-blue-700",
                    textColor: "text-white",
                    border: "any",
                    width: "w-full",
                    padding: "px-4 py-3",
                  }}
                />
              </div>
            </>
          )}
          <div className="py-5 bg-blue-gray-50">
            <Link href="/login">
              <a
                href={"/login"}
                className="text-sm font-medium text-gray-500 hover:text-blue-700"
              >
                <span aria-hidden="true">&larr; </span>Back to log in
              </a>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
